import { useEffect, useMemo, useState } from "react";
import type {
  RowSelectionState,
  SortingState,
  VisibilityState,
} from "@tanstack/react-table";
import type { ProductId } from "@KassaroAB/shared/types/ids";
import type {
  ProductSortColumn,
  ProductPresenceFilter,
} from "../../../data/productStore.types";
import {
  buildDefaultColumnVisibility,
  DEFAULT_VISIBLE_PRODUCT_COLUMN_IDS,
  type ProductColumnLabel,
} from "./productTableColumns";

const SEARCH_DEBOUNCE_MS = 300;
const DAY_MS = 24 * 60 * 60 * 1000;

export type StockWarningFilter = "out" | "low";

const SORTABLE_COLUMNS: ProductSortColumn[] = [
  "name",
  "articleNumber",
  "barcodeCode",
  "group",
  "type",
  "vat",
  "unitPrice",
  "stockCount",
  "updatedAt",
];

interface UseProductTableFiltersParams {
  columnLabels: ProductColumnLabel[];
}

export const useProductTableFilters = ({
  columnLabels,
}: UseProductTableFiltersParams) => {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [groupId, setGroupId] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [vatFilter, setVatFilter] = useState<number | null>(null);
  const [stockFilter, setStockFilter] = useState<ProductPresenceFilter | null>(null);
  const [bookkeepingAccountFilter, setBookkeepingAccountFilter] = useState<
    string | null
  >(null);
  const [variantsFilter, setVariantsFilter] =
    useState<ProductPresenceFilter | null>(null);
  const [supplierFilter, setSupplierFilter] = useState<string | null>(null);
  const [unitFilter, setUnitFilter] = useState<string | null>(null);
  const [updatedWithin, setUpdatedWithin] = useState<{
    days: number;
    since: string;
  } | null>(null);
  const setUpdatedWithinDays = (days: number | null) =>
    setUpdatedWithin(
      days === null
        ? null
        : {
            days,
            since: new Date(Date.now() - days * DAY_MS).toISOString(),
          },
    );
  const updatedWithinDays = updatedWithin?.days ?? null;
  const [priceMin, setPriceMin] = useState<number | null>(null);
  const [priceMax, setPriceMax] = useState<number | null>(null);
  const [stockWarningFilter, setStockWarningFilter] =
    useState<StockWarningFilter | null>(null);
  const [sorting, setSorting] = useState<SortingState>([
    { id: "updatedAt", desc: true },
  ]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() =>
    buildDefaultColumnVisibility(columnLabels),
  );
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const effectiveColumnVisibility = useMemo(() => {
    let changed = false;
    const next = { ...columnVisibility };
    columnLabels.forEach((column) => {
      if (
        !(column.id in columnVisibility) &&
        !DEFAULT_VISIBLE_PRODUCT_COLUMN_IDS.has(column.id)
      ) {
        next[column.id] = false;
        changed = true;
      }
    });
    return changed ? next : columnVisibility;
  }, [columnLabels, columnVisibility]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const sortColumn = useMemo<ProductSortColumn>(() => {
    const id = sorting[0]?.id;
    return id && SORTABLE_COLUMNS.includes(id as ProductSortColumn)
      ? (id as ProductSortColumn)
      : "updatedAt";
  }, [sorting]);

  const sortDirection = sorting[0]?.desc === false ? "asc" : "desc";

  const baseQuery = useMemo(
    () => ({
      search: debouncedSearch,
      groupId,
      type: typeFilter,
      vat: vatFilter,
      stockFilter,
      bookkeepingAccountFilter,
      variantsFilter,
      supplierFilter,
      unitFilter,
      updatedSince: updatedWithin?.since ?? null,
      priceMin,
      priceMax,
      sortColumn,
      sortDirection: sortDirection as "asc" | "desc",
    }),
    [
      debouncedSearch,
      groupId,
      typeFilter,
      vatFilter,
      stockFilter,
      bookkeepingAccountFilter,
      variantsFilter,
      supplierFilter,
      unitFilter,
      updatedWithin,
      priceMin,
      priceMax,
      sortColumn,
      sortDirection,
    ],
  );

  const activeFilterCount = [
    groupId,
    typeFilter,
    vatFilter,
    stockFilter,
    bookkeepingAccountFilter,
    variantsFilter,
    supplierFilter,
    unitFilter,
    updatedWithinDays,
    priceMin ?? priceMax,
    stockWarningFilter,
  ].filter((value) => value !== null).length;

  const clearFilters = () => {
    setGroupId(null);
    setTypeFilter(null);
    setVatFilter(null);
    setStockFilter(null);
    setBookkeepingAccountFilter(null);
    setVariantsFilter(null);
    setSupplierFilter(null);
    setUnitFilter(null);
    setUpdatedWithinDays(null);
    setPriceMin(null);
    setPriceMax(null);
    setStockWarningFilter(null);
  };

  const selectedIds = useMemo(
    () =>
      Object.entries(rowSelection)
        .filter(([, selected]) => selected)
        .map(([id]) => id as ProductId),
    [rowSelection],
  );

  return {
    search,
    setSearch,
    groupId,
    setGroupId,
    typeFilter,
    setTypeFilter,
    vatFilter,
    setVatFilter,
    stockFilter,
    setStockFilter,
    bookkeepingAccountFilter,
    setBookkeepingAccountFilter,
    variantsFilter,
    setVariantsFilter,
    supplierFilter,
    setSupplierFilter,
    unitFilter,
    setUnitFilter,
    updatedWithinDays,
    setUpdatedWithinDays,
    priceMin,
    setPriceMin,
    priceMax,
    setPriceMax,
    stockWarningFilter,
    setStockWarningFilter,
    activeFilterCount,
    clearFilters,
    sorting,
    setSorting,
    columnVisibility: effectiveColumnVisibility,
    setColumnVisibility,
    rowSelection,
    setRowSelection,
    selectedIds,
    baseQuery,
  };
};
